import React, { useMemo } from "react";
import { useGanttChartData } from "../../../../utils/reactQuery/epics";
import { useCustomSearchParams } from "../../../../hooks/useCustomSearchParams";
import Loader from "../../../../components/Loader";
import "./ganttchart.scss";

// Array of colors for tasks
const taskColors = [
  "#4285F4", // Blue
  "#EA4335", // Red
  "#FBBC05", // Yellow
  "#34A853", // Green
  "#8E24AA", // Purple
  "#16A2D7", // Light Blue
  "#FF6D00", // Orange
  "#00897B", // Teal
  "#C0CA33", // Lime
  "#D81B60", // Pink
];

const GantChartTimeline = () => {

  const [milestone] = useCustomSearchParams("milestone");
  const { ganttChartData, isLoading, isError } = useGanttChartData(milestone);
  const ganttData = ganttChartData?.data?.tasks || [];

  // Calculate date range for the timeline
 
 const calculateDateRange = () => {
    if (!ganttData || ganttData.length === 0) return { minDate: new Date(), maxDate: new Date() };

    let minDate = new Date(ganttData[0].start_date);
    let maxDate = new Date(ganttData[0].end_date);

    ganttData.forEach(task => {
      const startDate = new Date(task.start_date);
      const endDate = new Date(task.end_date);

      if (startDate < minDate) minDate = startDate;
      if (endDate > maxDate) maxDate = endDate;
    });
    maxDate.setDate(maxDate.getDate());
    return { minDate, maxDate };
  };

const {minDate,maxDate,totalDays} = useMemo(()=>{
  const {minDate,maxDate} = calculateDateRange()
  const totalDays = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24));
  return {minDate,maxDate,totalDays}
},[ganttData])

  const MAX_VISIBLE_DAYS = 7; // Maximum number of days visible on screen

  const groupTasksByUser = () => {
    const userGroups = {};

    ganttData.forEach(task => {
      if(task.duration_days >= 1){
        if (!userGroups[task.user_id]) {
          userGroups[task.user_id] = {
            userName: task.user_name,
          tasks: []
        };
      }
      userGroups[task.user_id].tasks.push(task);
    }});

    return Object.values(userGroups);
  };

const userGroups = useMemo(()=>{
  return groupTasksByUser()
},[ganttData])

  // Constants for workday calculations
  // Constants for workday calculations
  const WORK_HOURS_PER_DAY = 8;
  const WORK_START_HOUR = 0; // Work starts at 00:00 (midnight) and ends at 08:00
  
  const calculateTaskPosition = (task, index) => {
    const startDate = new Date(task.start_date);
    const endDate = new Date(task.end_date);

  
    // Get the total number of workdays from start to end
    const startDayIndex = Math.floor((startDate - minDate) / (1000 * 60 * 60 * 24));
    const endDayIndex = Math.floor((endDate - minDate) / (1000 * 60 * 60 * 24));

    // Get the work hour within that day
    let startHour = startDate.getHours();
    let endHour = endDate.getHours();
  
    // Ensure hours are within work hours (0-8)
    startHour = Math.min(Math.max(startHour, WORK_START_HOUR), WORK_HOURS_PER_DAY);
    endHour = Math.min(Math.max(endHour, WORK_START_HOUR), WORK_HOURS_PER_DAY);

    // Calculate the start and end work positions
    const startWorkHourPosition = startDayIndex * WORK_HOURS_PER_DAY + startHour;
    const endWorkHourPosition = endDayIndex * WORK_HOURS_PER_DAY + endHour;
 
    // Total working hours in the timeline
    const totalWorkHours = totalDays * WORK_HOURS_PER_DAY;
 
    // Convert to percentage for positioning
    let startPercentage = (startWorkHourPosition / totalWorkHours) * 100;
    let widthPercentage = ((endWorkHourPosition - startWorkHourPosition) / totalWorkHours) * 100 - 0.08;
   
    // Ensure minimum width for visibility
    // if (widthPercentage < 1) widthPercentage = 1;
  
    const colorIndex = index % taskColors.length;
    const backgroundColor = taskColors[colorIndex];
  
    return {
      left: `${startPercentage}%`,
      width: `${widthPercentage}%`,
      backgroundColor,
      position: "absolute",
    };
  };

if(totalDays === 0){
  return <div className="loader-center">No data found</div>
}
  return (
    <>
      {isLoading ? (
        <div className="loader-center">
          <Loader color={"black"} />
        </div>
      ) : (
        <div className="gantt-chart-container" style={{ "--visible-days": MAX_VISIBLE_DAYS }}>
          <table className={`gantt-table ${totalDays > MAX_VISIBLE_DAYS ? 'fixed-table-layout' : ''}`}>
            <thead>
              <tr>
                <th className="user-label-header">User Name</th>
                {Array.from({ length: totalDays }).map((_, index) => {
                  const date = new Date(minDate);
                  date.setDate(date.getDate() + index);
                  return (
                    <th key={`date-${index}`} className="date-column">
                      {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {userGroups.map((group, groupIndex) => (
                <tr key={`user-${groupIndex}`} className="user-row">
                  <td className="user-label">{group.userName}</td>
                  <td colSpan={totalDays} className="tasks-cell">
                    <div className="tasks-container">
                      {group.tasks.map((task, taskIndex) => {
                        const style = calculateTaskPosition(task, taskIndex);
                        return (
                          <div 
                            key={`task-${taskIndex}`} 
                            className="task-bar"
                            style={style}
                            title={`${task.title} (${new Date(task.start_date).toLocaleDateString()} - ${new Date(task.end_date).toLocaleDateString()})`}
                          >
                            <div className="task-title">{task.title}</div>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default GantChartTimeline;